import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import ProfileForm from './ProfileForm';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import type { ProfileFormData, Influencer } from '../../../types';

/**
 * Maps Influencer data + user phone data to ProfileFormData.
 * Phone fields come from the users table (via getUser), everything else from influencer.
 */
function mapToFormData(
  influencer: Influencer,
  userData: { nickname?: string; phoneNumber?: string; phoneVerified?: boolean; marketingConsentCollection?: boolean; marketingConsentPromotion?: boolean; marketingConsentEmail?: boolean; marketingConsentSMS?: boolean }
): ProfileFormData {
  return {
    name: influencer.name ?? '',
    nickname: userData.nickname || influencer.nickname || '',
    phoneNumber: userData.phoneNumber || influencer.phoneNumber || '',
    phoneVerified: userData.phoneVerified || influencer.phoneVerified || false,
    marketingConsentCollection: userData.marketingConsentCollection ?? false,
    marketingConsentPromotion: userData.marketingConsentPromotion ?? false,
    marketingConsentEmail: userData.marketingConsentEmail ?? false,
    marketingConsentSMS: userData.marketingConsentSMS ?? false,
  };
}

const MyInformationPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [userData, setUserData] = useState<{ nickname?: string; phoneNumber?: string; phoneVerified?: boolean; marketingConsentCollection?: boolean; marketingConsentPromotion?: boolean; marketingConsentEmail?: boolean; marketingConsentSMS?: boolean; oauthProvider?: string; kakaoId?: string; naverId?: string; googleId?: string }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.userId) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch influencer profile and user data in parallel
      const [influencerRes, userRes] = await Promise.all([
        graphqlApi.getInfluencerByUserId(user.userId),
        graphqlApi.getUser(user.userId),
      ]);

      if (influencerRes.success && influencerRes.data) {
        setInfluencer(influencerRes.data);
      } else {
        setError(influencerRes.error || 'Failed to load profile');
        return;
      }

      // Extract user data (nickname, phone, consent) from user table
      if (userRes.success && userRes.data) {
        const ud = (userRes.data as any).getUser || userRes.data;
        setUserData({
          nickname: ud.nickname,
          phoneNumber: ud.phoneNumber,
          phoneVerified: ud.phoneVerified,
          marketingConsentCollection: ud.marketingConsentCollection,
          marketingConsentPromotion: ud.marketingConsentPromotion,
          marketingConsentEmail: ud.marketingConsentEmail,
          marketingConsentSMS: ud.marketingConsentSMS,
          oauthProvider: ud.oauthProvider,
          kakaoId: ud.kakaoId,
          naverId: ud.naverId,
          googleId: ud.googleId,
        });
      }
    } catch (err: any) {
      console.error('Error fetching profile data:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Show a toast after returning from a 간편로그인 연결 round-trip
  // (OAuthCallbackHandler redirects here with ?connect=<provider>&status=...).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const connect = searchParams.get('connect');
    const status = searchParams.get('status');
    if (!connect || !status) return;
    if (status === 'success') {
      toast.success(t('myProfile.form.socialConnected', '간편로그인이 연결되었습니다.'));
      fetchData();
    } else if (status === 'conflict') {
      toast.error(t('myProfile.form.socialConflict', '이미 다른 계정에 연결된 소셜 계정입니다.'));
    } else {
      toast.error(t('myProfile.form.socialConnectFailed', '간편로그인 연결에 실패했습니다.'));
    }
    // Clear the query params so the toast doesn't repeat on refresh.
    searchParams.delete('connect');
    searchParams.delete('status');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams]);

  const handleSubmit = useCallback(
    async (data: ProfileFormData) => {
      if (!user?.userId) return;

      // Save to users table
      await graphqlApi.updateUser({
        userId: user.userId,
        name: data.name,
        nickname: data.nickname,
        marketingConsentCollection: data.marketingConsentCollection,
        marketingConsentPromotion: data.marketingConsentPromotion,
        marketingConsentEmail: data.marketingConsentEmail,
        marketingConsentSMS: data.marketingConsentSMS,
      } as any);

      // Save influencer-specific fields
      if (influencer?.influencerId) {
        await graphqlApi.updateInfluencer(influencer.influencerId, {
          name: data.name,
          nickname: data.nickname,
        });
      }
    },
    [user?.userId, influencer?.influencerId]
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-2 text-gray-600">
          {t('myProfile.form.loading', 'Loading...')}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors"
        >
          {t('myProfile.form.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <ProfileForm
      initialData={influencer ? mapToFormData(influencer, userData) : undefined}
      onSubmit={handleSubmit}
      isLoading={loading}
      hidePhoneVerification
      influencerId={influencer?.influencerId}
      oauthProvider={userData.oauthProvider}
      kakaoLinked={!!userData.kakaoId || userData.oauthProvider?.toLowerCase() === 'kakao'}
      naverLinked={!!userData.naverId || userData.oauthProvider?.toLowerCase() === 'naver'}
      googleLinked={!!userData.googleId || userData.oauthProvider?.toLowerCase() === 'google'}
      onSocialChanged={fetchData}
    />
  );
};

export default MyInformationPage;
